// Gesture synonyms — the client shape, and the rules for moving it to and from
// convorcher's own (ENG-1115).
//
// A synonym maps WORD STEMS onto one canonical gesture name: "grin,smirk" →
// "smile". Convorcher tries the stems BEFORE the gesture names themselves,
// longest stem first, as a `\bstem\w*` prefix regex over the lowercased action
// text (app/services/animation/animation.go, matchAnimation) — so "grin" also
// catches "grinning", and a longer stem always wins over a shorter one that
// also matches.
//
// THE WIRE SHAPE IS ONE COMMA-SEPARATED STRING per row: `{stemWords, gestureName}`
// (controllers/characters_api.go, gestureSynonymDTO), which the server splits on
// comma and trims, expanding each stem into its own key of the flat stem→gesture
// map (character_anim_repository.go, GetCharacterGestureSynonymsFlat). The editor
// holds the stems as an ARRAY — one chip each — because that is what it is; the
// join happens at the boundary. Nothing is lost either way: a stem containing a
// comma is not representable on the server side either.
//
// PUT replaces ALL rows for the character (SetCharacterGestureSynonyms), so the
// saver always sends the whole set — there is no per-row endpoint to mistake this
// for.
//
// A SYNONYM ONLY WORKS IF ITS TARGET IS IN THE CHARACTER'S GESTURE LIST —
// UNLESS THAT LIST IS EMPTY. NewAnimCache (animation.go) keeps only synonyms
// whose target is in the vocabulary, because an unfiltered mapping would match
// a name with no clip behind it AND bypass the motion rewriter, playing
// nothing. So an off-list mapping is dropped when the session opens and its
// stems never match at all.
//
// The exception is load-bearing: `filterSynonyms := len(sorted) > 0`. An empty
// vocabulary means "no character config", not "excludes everything", so every
// synonym survives — and a flag that condemned them all would be backwards.
//
// (convorcher's comment there calls the synonym map "a GLOBAL default while a
// character owns a subset". That is its rationale, not something observable
// from here: GetAnimationConfigForCharacter starts Synonyms as an EMPTY map and
// fills it only from this character's own rows, so at runtime there is no
// inherited set. Where a given character's rows came from is not knowable from
// this repo — convorcher's own admin UI can add them as free text.)
//
// But the comparison is NOT string equality. It runs through `exactAnimKey` —
// lowercase, keep only a-z0-9 — so "sigh" IS the listed "Sigh", and "look away"
// would be "LookAway". myra's rows store lowercase against a PascalCase gesture
// list, and comparing raw strings condemns a row that works. `vocabKey` is the
// port of that, and it is deliberately NOT `emittedGestureTag`: the emitted tag
// only folds whitespace, so the two disagree on any name carrying a hyphen or
// punctuation.

/** Split a wire `stemWords` string into the editor's stem array. */
export function stemList(stemWords) {
  return String(stemWords ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * The anim-config payload's `gestureSynonyms` as editor rows. Tolerates the
 * row already carrying an array (a caller that normalized twice) so this is
 * idempotent, and drops anything that is not an object.
 */
export function normalizeGestureSynonyms(animConfig) {
  const rows = animConfig?.gestureSynonyms
  if (!Array.isArray(rows)) return []
  return rows
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({
      stems: Array.isArray(r.stems) ? r.stems.map((s) => String(s).trim()).filter(Boolean) : stemList(r.stemWords),
      gestureName: String(r.gestureName ?? '').trim(),
    }))
}

/**
 * Editor rows → the wire array.
 *
 * A row missing EITHER half is dropped rather than sent. A synonym with no stem
 * can never match, and one with no gesture resolves to the empty animation name
 * — so a half-typed row is not a value the server should be asked to store, and
 * (because dirty state is compared on THIS projection) adding a blank row
 * neither marks the field dirty nor fires a PUT. The row itself stays visible in
 * the editor, labelled, so nothing is silently discarded on screen.
 */
export function toApiSynonyms(rows) {
  return (rows ?? [])
    .map((r) => ({
      stemWords: (r?.stems ?? []).map((s) => String(s).trim()).filter(Boolean).join(','),
      gestureName: String(r?.gestureName ?? '').trim(),
    }))
    .filter((r) => r.stemWords && r.gestureName)
}

/** Whether a row is complete enough to be saved (the inverse of what toApi drops). */
export function isSynonymRowComplete(row) {
  return (row?.stems ?? []).some((s) => String(s).trim()) && !!String(row?.gestureName ?? '').trim()
}

/** Dirty compare — on the wire projection, so incomplete rows are not "changes". */
export function synonymsEqual(a, b) {
  return JSON.stringify(toApiSynonyms(a)) === JSON.stringify(toApiSynonyms(b))
}

export function addSynonymRow(rows) {
  return [...(rows ?? []), { stems: [], gestureName: '' }]
}

export function removeSynonymRowAt(rows, index) {
  return (rows ?? []).filter((_, i) => i !== index)
}

/** Replace one row's fields; unknown indices are a no-op, never an append. */
export function setSynonymRow(rows, index, patch) {
  return (rows ?? []).map((r, i) => (i === index ? { ...r, ...patch } : r))
}

/**
 * The tag convorcher emits for a resolved gesture name — its own `ToPascalCase`
 * (app/lib/utils/strings/string_to_pascal.go): each space-separated word's
 * first letter uppercased, the rest left alone, joined. DISPLAY only.
 */
export function emittedGestureTag(name) {
  return String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join('')
}

/**
 * convorcher's `exactAnimKey` (animation.go): lowercase, then keep ONLY a-z0-9.
 * This — not the emitted tag — is what the vocabulary filter compares, so it is
 * what MEMBERSHIP must use.
 *
 * The two are not interchangeable and the gap is reachable: `exactAnimKey`
 * drops every non-alphanumeric, while `emittedGestureTag` only folds
 * whitespace. A stored "lean-in" against a listed "LeanIn" is a match on the
 * server and would be condemned as dead by the tag comparison.
 */
export function vocabKey(name) {
  return String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * Options for a row's gesture picker: the character's own gesture list, with a
 * saved-but-absent value preserved as a flagged option rather than dropped — the
 * `characterSelectOptions` precedent. Rewriting the value to one that exists
 * would hide exactly the thing worth seeing, and `missing` here means the row is
 * DEAD (see the header), not merely unusual.
 *
 * "Absent" is judged exactly as convorcher's vocabulary filter judges it — see
 * `vocabKey` — so "sigh" against a listed "Sigh" is NOT missing, and NOTHING is
 * missing when the gesture list is empty. Measured on myra, whose six stored
 * synonyms are all lowercase against a PascalCase gesture list: one is live and
 * five are not.
 */
export function gestureOptions(gestures, current) {
  const names = [...new Set((gestures ?? []).map((g) => String(g ?? '').trim()).filter(Boolean))]
  const opts = names.map((n) => ({ value: n, label: n, missing: false }))
  const cur = String(current ?? '').trim()
  if (!cur) return opts

  // Byte-identical to a listed gesture: nothing to add.
  if (names.includes(cur)) return opts

  // Same gesture, different spelling ("sigh" against a listed "Sigh"), which
  // convorcher's own exactAnimKey treats as a match — so this row is LIVE. The
  // option carrying it must be the STORED string, or the <select> matches no
  // option and renders "— pick a gesture —" over a configured row, observed on
  // myra. So the matched entry's value is swapped for the stored one and keeps
  // the listed name as its label: one option per gesture, showing what it
  // resolves to, selecting what is stored. Nothing is rewritten on the server.
  // No vocabulary configured → convorcher filters NOTHING (filterSynonyms is
  // gated on a non-empty list), so no row here is dead and the flag must stay
  // off. Without this the editor condemns every synonym of a character whose
  // Gestures field is simply empty.
  if (names.length === 0) return [{ value: cur, label: cur, missing: false }]

  const key = vocabKey(cur)
  const at = key ? opts.findIndex((o) => vocabKey(o.value) === key) : -1
  if (at >= 0) {
    opts[at] = { value: cur, label: opts[at].label, missing: false }
    return opts
  }

  // "(not in Gestures)" names the field it means; "(not in list)" left the
  // reader to guess which of the panel's several lists was meant.
  opts.push({ value: cur, label: `${cur} (not in Gestures)`, missing: true })
  return opts
}
